import React, { useMemo, useState } from "react";
import axios from "axios";
import { registerUser } from "../services/api";

type RegisterForm = {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
};

type FieldErrors = Partial<Record<keyof RegisterForm, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_PATTERN = /^[a-zA-Z0-9_-]{3,50}$/;

function calculatePasswordStrength(password: string): number {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[!@#$%^&*(),.?":{}|<>_\-[\]\\/~`+=;]/.test(password)) score += 1;
  return Math.min(score, 4);
}

function getPasswordStrengthLabel(strength: number): string {
  if (strength <= 1) return "Schwach";
  if (strength === 2) return "Mittel";
  if (strength === 3) return "Gut";
  return "Stark";
}

function getPasswordStrengthColor(strength: number): string {
  if (strength <= 1) return "bg-red-500";
  if (strength === 2) return "bg-yellow-500";
  if (strength === 3) return "bg-blue-500";
  return "bg-green-500";
}

function extractErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
  }

  const detail = error.response?.data?.detail;
  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => entry?.msg)
      .filter((msg): msg is string => typeof msg === "string");
    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
}

export default function RegisterPage() {
  const [form, setForm] = useState<RegisterForm>({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    termsAccepted: false,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showTerms, setShowTerms] = useState(false);

  const passwordStrength = useMemo(
    () => calculatePasswordStrength(form.password),
    [form.password]
  );

  function updateField<K extends keyof RegisterForm>(field: K, value: RegisterForm[K]) {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setServerError(null);
    setSuccessMessage(null);
  }

  function validateForm(): boolean {
    const nextErrors: FieldErrors = {};
    const username = form.username.trim();
    const email = form.email.trim();

    if (!username) {
      nextErrors.username = "Bitte Benutzername eingeben.";
    } else if (!USERNAME_PATTERN.test(username)) {
      nextErrors.username = "Bitte 3-50 Zeichen nutzen (Buchstaben, Zahlen, Unterstrich, Bindestrich).";
    }

    if (!email) {
      nextErrors.email = "Bitte E-Mail eingeben.";
    } else if (!EMAIL_PATTERN.test(email)) {
      nextErrors.email = "Bitte eine gültige E-Mail-Adresse eingeben.";
    }

    if (!form.password) {
      nextErrors.password = "Bitte Passwort eingeben.";
    } else {
      if (form.password.length < 8) {
        nextErrors.password = "Das Passwort muss mindestens 8 Zeichen haben.";
      } else if (!/[A-Z]/.test(form.password)) {
        nextErrors.password = "Das Passwort braucht mindestens einen Großbuchstaben.";
      } else if (!/[a-z]/.test(form.password)) {
        nextErrors.password = "Das Passwort braucht mindestens einen Kleinbuchstaben.";
      } else if (!/[0-9]/.test(form.password)) {
        nextErrors.password = "Das Passwort braucht mindestens eine Zahl.";
      }
    }

    if (!form.confirmPassword) {
      nextErrors.confirmPassword = "Bitte Passwort bestätigen.";
    } else if (form.confirmPassword !== form.password) {
      nextErrors.confirmPassword = "Die Passwörter stimmen nicht überein.";
    }

    if (!form.termsAccepted) {
      nextErrors.termsAccepted = "Bitte die Nutzungsbedingungen akzeptieren.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (loading) return;

    setServerError(null);
    setSuccessMessage(null);
    if (!validateForm()) return;

    setLoading(true);
    try {
      await registerUser({
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
      });

      setSuccessMessage(
        "Registrierung erfolgreich. Bitte jetzt anmelden."
      );
      setForm({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        termsAccepted: false,
      });
      setErrors({});
    } catch (error) {
      setServerError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 pt-16 pb-12">
      <div className="mx-auto w-full max-w-5xl">
        <div className="grid gap-10 rounded-3xl border border-indigo-100 bg-white/90 p-6 shadow-2xl backdrop-blur-sm md:grid-cols-2 md:p-10">
          <section className="order-2 md:order-1 md:pr-6">
            <p className="inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700">
              Willkommen bei LinkyShorty
            </p>
            <h1 className="mt-4 text-3xl font-bold text-gray-900 sm:text-4xl">
              Erstelle dein Konto
            </h1>
            <p className="mt-3 text-gray-600">
              Verwalte verkürzte Links und Analysen zentral in einem übersichtlichen Dashboard.
            </p>
            <div className="mt-8 space-y-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">Schneller Einstieg</p>
                <p className="mt-1 text-sm text-gray-600">
                  Erstelle dein Profil in weniger als einer Minute.
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-sm font-semibold text-gray-900">Sichere Zugangsdaten</p>
                <p className="mt-1 text-sm text-gray-600">
                  Passwortfelder sind verdeckt und werden vor dem Absenden validiert.
                </p>
              </div>
            </div>
          </section>

          <section className="order-1 md:order-2">
            <form onSubmit={onSubmit} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
              <h2 className="text-2xl font-semibold text-gray-900">Registrieren</h2>
              <p className="mt-1 text-sm text-gray-600">
                Benutzername, E-Mail und Passwort sind erforderlich.
              </p>

              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="username" className="mb-2 block text-sm font-medium text-gray-700">
                    Benutzername
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={form.username}
                    onChange={(event) => updateField("username", event.target.value)}
                    className={`w-full rounded-xl border px-4 py-4 sm:py-3 text-gray-900 text-base outline-none transition-all ${
                      errors.username
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    }`}
                    placeholder="max_mustermann"
                    aria-invalid={Boolean(errors.username)}
                    aria-describedby={errors.username ? "username-error" : undefined}
                  />
                  {errors.username && (
                    <p id="username-error" className="mt-2 text-sm text-red-700" role="alert">
                      {errors.username}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    E-Mail
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    className={`w-full rounded-xl border px-4 py-4 sm:py-3 text-gray-900 text-base outline-none transition-all ${
                      errors.email
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    }`}
                    placeholder="name@firma.de"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-2 text-sm text-red-700" role="alert">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">
                    Passwort
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(event) => updateField("password", event.target.value)}
                    className={`w-full rounded-xl border px-4 py-4 sm:py-3 text-gray-900 text-base outline-none transition-all ${
                      errors.password
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    }`}
                    placeholder="Passwort erstellen"
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? "password-error" : undefined}
                  />
                  <div className="mt-2">
                    <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
                      <span>Passwortstärke</span>
                      <span className="font-semibold text-gray-800">
                        {getPasswordStrengthLabel(passwordStrength)}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={`h-full transition-all ${getPasswordStrengthColor(passwordStrength)}`}
                        style={{ width: `${(passwordStrength / 4) * 100}%` }}
                      />
                    </div>
                  </div>
                  {errors.password && (
                    <p id="password-error" className="mt-2 text-sm text-red-700" role="alert">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="mb-2 block text-sm font-medium text-gray-700">
                    Passwort bestätigen
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    onChange={(event) => updateField("confirmPassword", event.target.value)}
                    className={`w-full rounded-xl border px-4 py-4 sm:py-3 text-gray-900 text-base outline-none transition-all ${
                      errors.confirmPassword
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                        : "border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    }`}
                    placeholder="Passwort erneut eingeben"
                    aria-invalid={Boolean(errors.confirmPassword)}
                    aria-describedby={errors.confirmPassword ? "confirmPassword-error" : undefined}
                  />
                  {errors.confirmPassword && (
                    <p id="confirmPassword-error" className="mt-2 text-sm text-red-700" role="alert">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <label className="inline-flex cursor-pointer items-start gap-3 text-sm text-gray-700">
                  <input
                    id="termsAccepted"
                    type="checkbox"
                    checked={form.termsAccepted}
                    onChange={(event) => updateField("termsAccepted", event.target.checked)}
                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-describedby={errors.termsAccepted ? "termsAccepted-error" : undefined}
                  />
                  <span>
                    Ich akzeptiere die Nutzungsbedingungen.{" "}
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-800"
                    >
                      Bedingungen lesen
                    </button>
                  </span>
                </label>
                {errors.termsAccepted && (
                  <p id="termsAccepted-error" className="mt-2 text-sm text-red-700" role="alert">
                    {errors.termsAccepted}
                  </p>
                )}
              </div>

              {serverError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  {serverError}
                </div>
              )}

              {successMessage && (
                <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700" role="status">
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 font-medium text-white transition-all hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:from-gray-300 disabled:to-gray-300"
              >
                {loading ? "Konto wird erstellt..." : "Konto erstellen"}
              </button>

              <p className="mt-4 text-center text-sm text-gray-600">
                Bereits registriert?{" "}
                <a href="#/login" className="font-semibold text-blue-700 hover:text-blue-800">
                  Zum Login wechseln
                </a>
              </p>
            </form>
          </section>
        </div>
      </div>

      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-xl font-semibold text-gray-900">Nutzungsbedingungen</h3>
            <div className="mt-4 max-h-72 space-y-3 overflow-y-auto text-sm leading-6 text-gray-600">
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer
                fringilla est nec volutpat vehicula. Sed consequat, sem at
                pellentesque consequat, dolor nunc vulputate felis, at tempor
                libero nulla eu lorem.
              </p>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce
                efficitur massa ac velit lobortis, id imperdiet arcu volutpat.
                Phasellus id orci at lectus eleifend luctus. Donec blandit
                convallis pellentesque.
              </p>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Curabitur
                feugiat suscipit urna, non interdum ipsum lacinia ac. Mauris
                interdum, ipsum at lacinia ultricies, est lorem placerat lectus,
                nec iaculis neque velit eu elit.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
